// Shared data builder for Getting Started activities used by LaunchPad and Drawer
import { getDealRoomBrandedName } from '../../utils/locale';

const defaultActivities = [
  'consultation',
  'profile',
  'valuation',
  'emergency_planning',
  'deal_room',
  'education',
  'discovery',
];

// RJ Seller Visibility Workflow — id of the marketplace visibility card.
// Exported so LaunchPad can exclude it from the "Start Here" / "Next Step"
// badge and from the quickstart collapse condition without re-declaring the
// string in three places.
export const MARKETPLACE_VISIBILITY_ID = 'marketplace_visibility';

const hasFeature = (features = [], featureName) => {
  if (!Array.isArray(features)) return false;
  return features.some((f) => String(f) === featureName || String(f) === `${featureName}_type`);
};

export default function useGettingStartedData(current_user = {}, company = {}, features = [], options = {}, intl = null) {
  const source = options && options.source ? String(options.source) : undefined;
  const defaultMotivation = options && options.defaultMotivation ? String(options.defaultMotivation) : undefined;
  // assessmentResult: optional ReadinessAssessmentResult object from the backend.
  // When provided and completed, its suggestedActivityIds override the default ordering.
  const assessmentResult = (options && options.assessmentResult) ? options.assessmentResult : null;
  // Merge Redux store user but prefer props to avoid stale store overriding server props
  const storeUser = (() => { try { return (typeof document !== 'undefined' && document.store?.getState?.()?.user) || {}; } catch(_) { return {}; } })();
  const user = { ...storeUser, ...current_user };
  const flagEnabled = Array.isArray(features) ? features.includes('triggering_event_getting_started') : false;
  const events = Array.isArray(user.triggering_events) ? user.triggering_events : [];
  const hasEvent = (type) => events.some((e) => e.event_type === type);
  const multiLineValuationEnabled = Array.isArray(features) ? features.includes('multi_line_valuation') : false;
  const customValuationEnabled = Array.isArray(features) ? features.includes('custom_valuation') : false;
  const valuationEnabled = multiLineValuationEnabled || customValuationEnabled;
  const continuityBuilderEnabled = Array.isArray(features) ? features.includes('continuity_builder') : false;
  const planBuilderEnabled = hasFeature(features, 'plan_builder');
  const loanRepaymentCalculatorEnabled = hasFeature(features, 'loan_repayment_calculator');
  const buyerReadinessEnabled = Array.isArray(features) ? (features.includes('buyer_readiness_assessment_full') || features.includes('buyer_readiness_assessment_quick')) : false;
  const rolodexReviewEnabled = Array.isArray(features) && features.includes('rolodex') && features.includes('simple_marketplace_approval');

  const isActivityCompleted = (activityId) => {
  // Prefer current_user first (matches Drawer prop usage), then fall back to store
  const candidates = [current_user || {}, storeUser || {}, user || {}];
    const any = (fn) => candidates.some((u) => fn(u));
    switch (activityId) {
      // RJ Seller Visibility — "completed" here means "currently opted in", so
      // the card renders with a check when visibility is on. It is reversible
      // state, not a finished task: `excludeFromProgress` keeps this out of the
      // completed/total counts, and LaunchPad keeps it out of the Start Here
      // badge, so a seller who has opted out is never nagged toward opting in.
      case MARKETPLACE_VISIBILITY_ID:
        return any((u) => u.marketplace_visibility_opt_in === true);
      case 'find_my_gaps':
        return any((u) => {
          // Check for succession-type plans in mySuccessionPlans (comprehensive succession plan)
          const successionPlanSlugs = [
            'comprehensive-succession-plan-v2',
            'comprehensive-succession-plan',
          ];
          
          const plans = Array.isArray(u.my_succession_plans) ? u.my_succession_plans : 
                        (Array.isArray(u.mySuccessionPlans) ? u.mySuccessionPlans : []);
          
          // Check if user has started or completed a succession plan
          const hasSuccessionPlan = plans.some((plan) => {
            const templateSlug = plan?.planTemplate?.slug || plan?.plan_template?.slug || '';
            const planType = plan?.planTemplate?.planType || plan?.plan_template?.plan_type || '';
            const isSuccessionPlan = successionPlanSlugs.includes(templateSlug) || planType === 'succession';
            const isStartedOrCompleted = plan?.status === 'in_progress' || plan?.status === 'completed' || 
                                         (plan?.completionPercentage || plan?.completion_percentage || 0) > 0;
            return isSuccessionPlan && isStartedOrCompleted;
          });
          
          if (hasSuccessionPlan) return true;
          
          // Legacy fallbacks
          const explicit = u['has_completed_triggering_events?'];
          if (explicit !== undefined && explicit !== null) return Boolean(explicit);
          if (u.has_completed_triggering_events !== undefined) return Boolean(u.has_completed_triggering_events);
          const evts = Array.isArray(u.triggering_events) ? u.triggering_events : [];
          if (evts.some((e) => e && e.completed_at)) return true;
          
          return false;
        });
      case 'valuation':
        return any((u) => u.valuation_last_accessed_date !== null);
      case 'profile':
        return any((u) => u.profile_last_accessed_date !== null);
      case 'discovery':
        return any((u) => u['has_used_discovery?']);
      case 'bcp':
        return any((u) => (u.bcp_form_datum && (u.bcp_form_datum.status === 'in_progress' || u.bcp_form_datum.status === 'completed')));
      case 'catastrophe':
        return any((u) => {
          const catastrophicPlanSlugs = ['catastrophic-plan-v1'];
          const plans = Array.isArray(u.my_succession_plans) ? u.my_succession_plans :
                        (Array.isArray(u.mySuccessionPlans) ? u.mySuccessionPlans : []);
          const hasCatastrophicPlan = plans.some((plan) => {
            const templateSlug = plan?.planTemplate?.slug || plan?.plan_template?.slug || '';
            const planType = plan?.planTemplate?.planType || plan?.plan_template?.plan_type || '';
            const isCatastrophicPlan = catastrophicPlanSlugs.includes(templateSlug) || planType === 'catastrophic';
            const isStartedOrCompleted = plan?.status === 'in_progress' || plan?.status === 'completed' ||
                                         (plan?.completionPercentage || plan?.completion_percentage || 0) > 0;
            return isCatastrophicPlan && isStartedOrCompleted;
          });
          if (hasCatastrophicPlan) return true;
          // When continuity_builder feature is on, also check CB workflow_state >= in_progress
          if (continuityBuilderEnabled) {
            const state = u.continuity_builder_workflow_state;
            return state === 'in_progress' || state === 'filed_agreement' || state === 'seal_issued';
          }
          return false;
        });
      case 'emergency_planning':
        // When continuity_builder feature is on, completion = CB workflow_state >= in_progress
        if (continuityBuilderEnabled) {
          return any((u) => {
            const state = u.continuity_builder_workflow_state;
            return state === 'in_progress' || state === 'filed_agreement' || state === 'seal_issued';
          });
        }
        // Completed if either continuity or catastrophic PlanTemplate has been completed
        // Slugs: continuity-plan-cad-v1, continuity-plan-us-v1, catastrophic-plan-v1, catastrophic-plan-us-v1
        return any((u) => {
          const emergencyPlanSlugs = [
            'continuity-plan-cad-v1',
            'continuity-plan-us-v1',
            'catastrophic-plan-v1',
            'catastrophic-plan-us-v1',
          ];

          const plans = Array.isArray(u.my_succession_plans) ? u.my_succession_plans :
                        (Array.isArray(u.mySuccessionPlans) ? u.mySuccessionPlans : []);
          return plans.some((plan) => {
            const templateSlug = plan?.planTemplate?.slug || plan?.plan_template?.slug || '';
            const isEmergencyPlan = emergencyPlanSlugs.includes(templateSlug);
            const isCompleted = plan?.status === 'completed' || plan?.completionPercentage === 100 || plan?.completion_percentage === 100;
            return isEmergencyPlan && isCompleted;
          });
        });
      case 'succession_planning':
        return any((u) => {
          // Check for succession-type plans in mySuccessionPlans (same as find_my_gaps)
          const successionPlanSlugs = [
            'comprehensive-succession-plan-v2',
            'comprehensive-succession-plan',
          ];
          
          const plans = Array.isArray(u.my_succession_plans) ? u.my_succession_plans : 
                        (Array.isArray(u.mySuccessionPlans) ? u.mySuccessionPlans : []);
          
          const hasSuccessionPlan = plans.some((plan) => {
            const templateSlug = plan?.planTemplate?.slug || plan?.plan_template?.slug || '';
            const planType = plan?.planTemplate?.planType || plan?.plan_template?.plan_type || '';
            const isSuccessionPlan = successionPlanSlugs.includes(templateSlug) || planType === 'succession';
            const isStartedOrCompleted = plan?.status === 'in_progress' || plan?.status === 'completed' || 
                                         (plan?.completionPercentage || plan?.completion_percentage || 0) > 0;
            return isSuccessionPlan && isStartedOrCompleted;
          });
          return hasSuccessionPlan;
        });
      case 'education':
        return any((u) => Boolean(u['has_viewed_education?']) || u.has_viewed_education === true || u.education_last_accessed_date != null || u.education_viewed_at != null);
      case 'obtain_financing':
        return any((u) => Boolean(u['has_viewed_financing?']) || u.has_viewed_financing === true);
      case 'wealth_gap':
        if (flagEnabled) return hasEvent('wealth_gap');
        return any((u) => u['has_wealth_gap_data?'] === true || u.has_wealth_gap_data === true);
      case 'owner_diagnostic':
        return flagEnabled ? hasEvent('owner_diagnostic') : any((u) => u.diag_completed_at != null);
      case 'profit_gap':
        if (flagEnabled) return hasEvent('profit_gap');
        return any((u) => {
          const isWealth = (company && company.business_type) === 'wealth_rj';
          if (u.profit_gap_recasted_ebitda != null) return true;
          if (isWealth) return u.gross_production != null && u.normalized_operating_expenses != null;
          return u.recurring_revenue != null && u.normalized_operating_expenses != null;
        });
      case 'value_gap': {
        if (flagEnabled) return hasEvent('value_gap');
        const prereqs = (isActivityCompleted('wealth_gap') && isActivityCompleted('profit_gap') && isActivityCompleted('owner_diagnostic'));
        return prereqs && any((u) => u.value_gap_current_value !== null && u.value_gap_gap_value !== null);
      }
      case 'consultation':
        return any((u) => u.consultation_booked_at !== null);
      case 'deal_room':
        return any((u) => u.inventory_user && u.inventory_user.deals_count > 0);
      case 'buyer_readiness':
        return any((u) => u.buyer_assessment_completed_at != null);
      default:
        return false;
    }
  };

  // Determine suggested activities
  const motivation = (current_user?.motivation ?? storeUser?.motivation ?? defaultMotivation);
  let suggestedActivities = defaultActivities;
  // Callers can pass `suggested_activities: null` (explicitly, not undefined) as a
  // signal to skip the Redux store fallback — e.g. when motivation just changed at
  // runtime and the server-rendered list is stale.
  const callerExplicitlyClearedActivities =
    current_user && Object.prototype.hasOwnProperty.call(current_user, 'suggested_activities') &&
    current_user.suggested_activities === null;
  const provided = callerExplicitlyClearedActivities
    ? null
    : (Array.isArray(current_user?.suggested_activities) && current_user.suggested_activities.length > 0
        ? current_user.suggested_activities
        : (Array.isArray(storeUser?.suggested_activities) && storeUser.suggested_activities.length > 0 ? storeUser.suggested_activities : null));

  // Priority order:
  // 1. Per-user suggested_activities field (explicit override) — plus growth/rolodex appends
  // 2. Completed assessment result: suggestedActivityIds places gap-covering cards first
  // 3. Succession motivation default ordering
  // 4. Platform default
  if (provided) {
    suggestedActivities = provided;
    if (motivation === 'growth' && loanRepaymentCalculatorEnabled) {
      suggestedActivities = [...suggestedActivities, 'obtain_financing'];
    }
    if (motivation === 'growth' && buyerReadinessEnabled) {
      suggestedActivities = [...suggestedActivities, 'buyer_readiness'];
    }
    if (rolodexReviewEnabled) {
      suggestedActivities = [...suggestedActivities, 'review_rolodex'];
    }
  } else if (assessmentResult && assessmentResult.completed && Array.isArray(assessmentResult.suggestedActivityIds) && assessmentResult.suggestedActivityIds.length > 0) {
    suggestedActivities = assessmentResult.suggestedActivityIds;
  } else if (motivation === 'succession') {
    // 6 cards for succession-motivated users in specific order
    suggestedActivities = ['consultation', 'profile', 'valuation', 'catastrophe', 'find_my_gaps', 'deal_room'];
    if (rolodexReviewEnabled) {
      suggestedActivities.push('review_rolodex');
    }
  } else if (motivation === 'growth') {
    // Growth-motivated users see catastrophe instead of emergency_planning
    suggestedActivities = ['consultation', 'profile', 'valuation', 'catastrophe', 'deal_room', 'education', 'discovery'];
    if (loanRepaymentCalculatorEnabled) {
      suggestedActivities.push('obtain_financing');
    }
    if (buyerReadinessEnabled) {
      suggestedActivities.push('buyer_readiness');
    }
    if (rolodexReviewEnabled) {
      suggestedActivities.push('review_rolodex');
    }
  }
  // Remove valuation when neither custom_valuation nor multi_line_valuation is enabled
  if (!valuationEnabled) {
    suggestedActivities = suggestedActivities.filter(a => a !== 'valuation');
  }

  // Remove plan-builder-specific activities when plan_builder is disabled.
  // Catastrophe is gated separately by continuity_builder.
  if (!planBuilderEnabled) {
    suggestedActivities = suggestedActivities.filter(a => a !== 'find_my_gaps' && a !== 'emergency_planning');
  }

  // Catastrophic planning activity is controlled by continuity_builder.
  if (!continuityBuilderEnabled) {
    suggestedActivities = suggestedActivities.filter(a => a !== 'catastrophe');
  }

  // Remove deal_room when deal_room feature is disabled
  const dealRoomEnabled = Array.isArray(features) ? features.includes('deal_room') : false;
  if (!dealRoomEnabled) {
    suggestedActivities = suggestedActivities.filter(a => a !== 'deal_room');
  }

  // Remove consultation when disabled_book_a_consultation feature is enabled
  const consultationDisabled = Array.isArray(features) && features.includes('disabled_book_a_consultation');
  if (consultationDisabled) {
    suggestedActivities = suggestedActivities.filter(a => a !== 'consultation');
  }

  // RJ Seller Visibility Workflow — marketplace visibility card.
  //
  // Appended AFTER the branch chain above, deliberately. `suggestedActivities`
  // has four possible sources (server-provided list, completed assessment
  // ordering, succession default, platform default) and only one of them is the
  // succession branch. Putting this inside that branch would hide the card from
  // any seller who has a server-provided list or a completed readiness
  // assessment — which is most of them. Appending here makes it independent of
  // which ordering won.
  //
  // Gate: tenant flag ON + succession motivation + not awaiting manager
  // approval. A seller whose motivation change is still pending stays fully
  // dark (no card, no modal, no teaser) per the 14 Sep 2026 decision.
  const rjSellerVisibilityEnabled = Array.isArray(features) && features.includes('rj_seller_visibility');
  const approvalState = user?.approval_state;
  const approvalPending = approvalState === 'pending' || approvalState === 0;
  const showMarketplaceVisibility =
    rjSellerVisibilityEnabled && motivation === 'succession' && !approvalPending;
  // Current opt-in state drives the card's title and description. Read from the
  // caller first (LaunchPad overlays the live GraphQL value) then the store.
  const marketplaceVisibilityOptIn =
    current_user?.marketplace_visibility_opt_in ?? storeUser?.marketplace_visibility_opt_in ?? false;
  if (showMarketplaceVisibility) {
    suggestedActivities = [...suggestedActivities, MARKETPLACE_VISIBILITY_ID];
  } else {
    suggestedActivities = suggestedActivities.filter((a) => a !== MARKETPLACE_VISIBILITY_ID);
  }

  suggestedActivities = Array.from(new Set(suggestedActivities));

  // Activity map
  const t = (id, values) => intl ? intl.formatMessage({ id }, values) : null;
  const dealRoomWord = getDealRoomBrandedName(company);
  const activityMap = {
    find_my_gaps: { id: 'find_my_gaps', title: t('succession.launchpad.activity_find_my_gaps_title') || 'Create Your Succession Plan', shortDescription: t('succession.launchpad.activity_find_my_gaps_desc') || 'Build your comprehensive succession plan with AI guidance.', icon: 'TargetIcon', path: '/succession/plans', bgColor: 'bg-succession-primary/10', iconColor: 'text-succession-primary', borderColor: 'border-succession-border hover:border-succession-primary' },
    emergency_planning: { id: 'emergency_planning', title: t('succession.launchpad.activity_emergency_planning_title') || 'Emergency Planning', shortDescription: t('succession.launchpad.activity_emergency_planning_desc') || 'Create continuity or catastrophic plans for emergencies.', icon: 'ExclamationCircleIcon', path: '/succession/plans/new', bgColor: 'bg-succession-warning/10', iconColor: 'text-succession-warning', borderColor: 'border-succession-border hover:border-succession-warning' },
    wealth_gap: { id: 'wealth_gap', title: t('succession.launchpad.activity_wealth_gap_title') || "What's Your Wealth Gap?", shortDescription: t('succession.launchpad.activity_wealth_gap_desc') || 'Calculate your Wealth Gap and next steps.', icon: 'CurrencyDollarIcon', path: '/succession/readiness/wealth-gap', bgColor: 'bg-succession-primary/10', iconColor: 'text-succession-primary', borderColor: 'border-succession-border hover:border-succession-primary' },
    owner_diagnostic: { id: 'owner_diagnostic', title: t('succession.launchpad.activity_owner_diagnostic_title') || 'Business Attractiveness vs. Buyer Readiness', shortDescription: t('succession.launchpad.activity_owner_diagnostic_desc') || 'See your quadrant: attractiveness vs readiness.', icon: 'ClipboardDocumentIcon', path: '/succession/readiness/owner-diagnostic', bgColor: 'bg-succession-primary/10', iconColor: 'text-succession-primary', borderColor: 'border-succession-border hover:border-succession-primary' },
    profit_gap: { id: 'profit_gap', title: t('succession.launchpad.activity_profit_gap_title') || 'What is your Profit Gap?', shortDescription: t('succession.launchpad.activity_profit_gap_desc') || 'Benchmark business profits against best-in-class peers.', icon: 'ChartBarIcon', path: '/succession/readiness/profit-gap', bgColor: 'bg-succession-info/10', iconColor: 'text-succession-info', borderColor: 'border-succession-border hover:border-succession-primary' },
    value_gap: { id: 'value_gap', title: t('succession.launchpad.activity_value_gap_title') || 'Value Gap', shortDescription: t('succession.launchpad.activity_value_gap_desc') || 'Compare current value to your wealth goal.', icon: 'CurrencyDollarIcon', path: '/succession/readiness/value-gap', bgColor: 'bg-succession-primary/10', iconColor: 'text-succession-primary', borderColor: 'border-succession-border hover:border-succession-primary' },
    valuation: { id: 'valuation', title: t('succession.launchpad.activity_valuation_title') || 'Run Valuation', shortDescription: t('succession.launchpad.activity_valuation_desc') || 'Estimate practice value with DCF and MOR.', icon: 'ChartBarIcon', path: (multiLineValuationEnabled ? '/succession/valuation/multi' : '/succession/valuation'), bgColor: 'bg-succession-primary/10', iconColor: 'text-succession-primary', borderColor: 'border-succession-border hover:border-succession-primary' },
    profile: { id: 'profile', title: t('succession.launchpad.activity_profile_title') || 'Edit Profile', shortDescription: t('succession.launchpad.activity_profile_desc') || 'Update profile to personalize your experience.', icon: 'UserIcon', path: '/succession/profile', bgColor: 'bg-succession-success/10', iconColor: 'text-succession-success', borderColor: 'border-succession-border hover:border-succession-success' },
    discovery: { id: 'discovery', title: t('succession.launchpad.activity_discovery_title') || 'Find Partners', shortDescription: t('succession.launchpad.activity_discovery_desc') || 'Find and connect with potential partners.', icon: 'SearchIcon', path: '/succession/search', bgColor: 'bg-succession-info/10', iconColor: 'text-succession-info', borderColor: 'border-succession-border hover:border-succession-primary' },
    bcp: { id: 'bcp', title: t('succession.launchpad.activity_bcp_title') || 'Create Continuity Plan', shortDescription: t('succession.launchpad.activity_bcp_desc') || 'Build a simple continuity plan for emergencies.', icon: 'DocumentTextIcon', path: '/succession/bcp', bgColor: 'bg-succession-warning/10', iconColor: 'text-succession-warning', borderColor: 'border-succession-border hover:border-succession-warning' },
    catastrophe: { id: 'catastrophe', title: t('succession.launchpad.activity_catastrophe_title') || 'Catastrophic Planning', shortDescription: t('succession.launchpad.activity_catastrophe_desc') || 'Prepare for unexpected events with an emergency plan.', icon: 'ExclamationCircleIcon', path: '/succession/pact-builder', bgColor: 'bg-succession-warning/10', iconColor: 'text-succession-warning', borderColor: 'border-succession-border hover:border-succession-warning' },
    succession_planning: { id: 'succession_planning', title: t('succession.launchpad.activity_succession_planning_title') || 'Succession Planning', shortDescription: t('succession.launchpad.activity_succession_planning_desc') || 'Design your comprehensive succession plan.', icon: 'ClipboardDocumentIcon', path: '/succession/succession_planning', bgColor: 'bg-succession-primary/10', iconColor: 'text-succession-primary', borderColor: 'border-succession-border hover:border-succession-primary' },
    education: { id: 'education', title: t('succession.launchpad.activity_education_title') || 'Educational Library', shortDescription: t('succession.launchpad.activity_education_desc') || 'Browse articles and resources to learn more.', icon: 'AcademicCapIcon', path: '/succession/education', bgColor: 'bg-succession-info/10', iconColor: 'text-succession-info', borderColor: 'border-succession-border hover:border-succession-primary' },
    consultation: { id: 'consultation', title: t('succession.launchpad.activity_consultation_title') || 'Book a Consultation', shortDescription: t('succession.launchpad.activity_consultation_desc') || 'Book time with a platform concierge.', icon: 'CalendarIcon', path: (company && (company.calendly_link || company.calendlyLink)) || 'https://calendly.com/findbob/consultation', bgColor: 'bg-succession-bg-secondary', iconColor: 'text-succession-text-secondary', borderColor: 'border-succession-border hover:border-succession-border-hover' },
    deal_room: { id: 'deal_room', title: t('succession.launchpad.activity_deal_room_title', { word: dealRoomWord }) || `Create a ${dealRoomWord}`, shortDescription: t('succession.launchpad.activity_deal_room_desc') || 'Set sell-side objectives and manage the process.', icon: 'BuildingOfficeIcon', path: '/succession/deals', bgColor: 'bg-succession-bg-secondary', iconColor: 'text-succession-text-secondary', borderColor: 'border-succession-border hover:border-succession-border-hover' },
    buyer_readiness: { id: 'buyer_readiness', title: 'Buyer Readiness', shortDescription: 'Assess your readiness for acquisition', icon: 'ClipboardDocumentCheckIcon', path: '/succession/buyer-readiness-assessment', bgColor: 'bg-succession-info/10', iconColor: 'text-succession-info', borderColor: 'border-succession-border hover:border-succession-info' },
    review_rolodex: { id: 'review_rolodex', title: 'Review Your Rolodex', shortDescription: 'Track expressions of interest and follow up on saved contacts.', icon: 'ClipboardDocumentIcon', path: '/succession/rolodex', bgColor: 'bg-succession-info/10', iconColor: 'text-succession-info', borderColor: 'border-succession-border hover:border-succession-primary' },
    // RJ Seller Visibility Workflow — state-reflective, NOT a completable task.
    //
    // `excludeFromProgress` keeps it out of the completed/total maths below, and
    // `alwaysVisible` tells LaunchPad to keep the quickstart block mounted even
    // when every real activity is done. Both are needed: excluding it from the
    // count alone would still let the block collapse and take the card with it.
    //
    // `path` is a no-op fallback — the card opens an inline sheet via an onClick
    // hint rather than navigating. Settings is where it lands if JS is degraded.
    [MARKETPLACE_VISIBILITY_ID]: {
      id: MARKETPLACE_VISIBILITY_ID,
      title: marketplaceVisibilityOptIn
        ? (t('succession.launchpad.activity_marketplace_visibility_title_on') || 'Review Your Visibility')
        : (t('succession.launchpad.activity_marketplace_visibility_title_off') || 'Make yourself visible to buyers'),
      shortDescription: marketplaceVisibilityOptIn
        ? (t('succession.launchpad.activity_marketplace_visibility_desc_on') || 'Buyers in your division can find your profile. Change this at any time.')
        : (t('succession.launchpad.activity_marketplace_visibility_desc_off') || 'Opt in so approved buyers in your division can find and contact you.'),
      icon: 'EyeIcon',
      path: '/succession/settings',
      excludeFromProgress: true,
      alwaysVisible: true,
      bgColor: 'bg-succession-info/10',
      iconColor: 'text-succession-info',
      borderColor: 'border-succession-border hover:border-succession-primary',
    },
  };

  const activities = (suggestedActivities || []).map((id) => activityMap[id]).filter(Boolean);
  // Progress is computed over completable activities only. Cards flagged
  // `excludeFromProgress` (currently just the RJ marketplace visibility card)
  // still render, but reversible state must not move the "X of Y complete"
  // counter — otherwise opting out would read as losing progress, and opting in
  // would silently collapse the guide.
  const progressActivities = activities.filter((a) => !a.excludeFromProgress);
  const total = progressActivities.length;
  const completed = progressActivities.filter((a) => isActivityCompleted(a.id)).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  // True when at least one rendered card must outlive the guide's collapse
  // condition. LaunchPad reads this to keep the block mounted.
  const hasAlwaysVisibleActivity = activities.some((a) => a.alwaysVisible);
  try {
    if (typeof window !== 'undefined') {
      const stack = (new Error()).stack || '';
      const from = (stack.split('\n')[2] || '').trim();
      window.__gsDebug = Array.isArray(window.__gsDebug) ? window.__gsDebug : [];
      window.__gsDebug.push({
        from,
        source,
        motivation,
        motivationSources: { current_user_motivation: current_user?.motivation, store_user_motivation: storeUser?.motivation, defaultMotivation },
        userIds: { props: current_user?.id, store: storeUser?.id },
        suggestedActivities,
        completedIds: activities.filter((a) => isActivityCompleted(a.id)).map((a) => a.id),
      });
      // Provide non-underscored alias for convenience
      window.gsDebug = window.__gsDebug;
      // eslint-disable-next-line no-console
      console.log('[useGettingStartedData] debug', window.__gsDebug[window.__gsDebug.length - 1]);
    }
  } catch (_) {}

  return { activities, isActivityCompleted, percent, completed, total, hasAlwaysVisibleActivity };
}


